/**
 * One reading of a polled query's truth [mv2-D4].
 *
 * React Query keeps the last good `data` through a failed or hung refetch, so
 * `data ?? null` shows yesterday's answer as today's. Every verdict and every
 * `QueryFrame` reads a query through `liveQueryState` instead, which names the
 * phase and writes the one sentence the operator sees for it:
 *
 * | phase               | message                                              |
 * |---------------------|------------------------------------------------------|
 * | idle                | none (a disabled query)                              |
 * | checking            | `Checking ‹what›…`                                   |
 * | refreshing          | `Refreshing ‹what›…`                                 |
 * | error-before-data   | `Could not load ‹what›: ‹msg›`                       |
 * | overdue-before-data | `‹Panel› has not answered within N s`                |
 * | error-after-data    | `‹Panel› not refreshed since T — ‹msg›`              |
 * | overdue             | `‹Panel› not refreshed since T — no answer within N s` |
 * | live                | none                                                 |
 *
 * `T` is `formatLocalTime(lastGoodAtMs)`; `N` is `round(maxAgeMs / 1000)`;
 * `‹msg›` is `describeError(error)` (an `ApiError` already carries ` (ref ‹id›)`).
 * Unknown is never live: no data, an error, or an answer older than its maximum
 * age each has its own phase [S4].
 *
 * Pure and runnable under `node --test`: runtime imports are relative with the
 * `.ts` extension, and nothing here imports the API client (it reads
 * `import.meta.env` when it loads).
 */
import { formatLocalTime } from './format.ts';

/** The fields of a React Query result this reading needs; a plain object in tests. */
export interface LiveQueryLike<T> {
  data: T | undefined;
  error: unknown;
  dataUpdatedAt: number;
  status?: 'pending' | 'error' | 'success';
  fetchStatus?: 'fetching' | 'paused' | 'idle';
  isPlaceholderData?: boolean;
}

/** `watchSinceMs` = max(mount, last return to a visible tab). */
export interface FreshnessClock {
  nowMs: number;
  watchSinceMs: number;
}

export type LiveQueryPhase =
  | 'idle'
  | 'checking'
  | 'refreshing'
  | 'error-before-data'
  | 'overdue-before-data'
  | 'error-after-data'
  | 'overdue'
  | 'live';

export interface LiveQueryState<T> {
  phase: LiveQueryPhase;
  /** Last good answer; `undefined` before the first one (placeholder data is not an answer). */
  data: T | undefined;
  error: unknown;
  lastGoodAtMs: number | null;
  message: string | null;
  /** The failed request's id when the error carries one (`ApiError.requestId`). */
  requestId: string | null;
}

/** A source whose freshness can expire: its last answer and its maximum age. */
export interface FreshnessSource {
  dataUpdatedAt: number;
  maxAgeMs: number;
}

/**
 * The one staleness formula: two missed polls plus 4 s of latency, never under
 * 10 s. 2 s → 10 s, 5 s → 14 s, 10 s → 24 s, 30 s → 64 s.
 */
export const staleAfterMs = (pollMs: number): number => Math.max(10_000, 2 * pollMs + 4_000);

/**
 * Error text for any thrown value. React Query can hold a plain `Error` (for
 * example `data is undefined` from a 204) or something that is not an Error at
 * all, so this duck-types instead of assuming `ApiError`:
 * `toDisplayString()` when it is a function, else `message`, else `String(e)`;
 * `null`/`undefined` read `no response`. Never throws.
 */
export function describeError(e: unknown): string {
  if (e === null || e === undefined) return 'no response';
  if (typeof e === 'object') {
    const candidate = e as { toDisplayString?: unknown; message?: unknown };
    if (typeof candidate.toDisplayString === 'function') {
      try {
        const text: unknown = (candidate.toDisplayString as () => unknown).call(e);
        if (typeof text === 'string' && text !== '') return text;
      } catch {
        // fall through to the message
      }
    }
    if (typeof candidate.message === 'string' && candidate.message !== '') return candidate.message;
  }
  if (typeof e === 'string') return e;
  if (typeof e === 'number' || typeof e === 'boolean' || typeof e === 'bigint') return String(e);
  if (typeof e === 'symbol') return e.toString();
  try {
    // String(e) for an object or function, without assuming it has a prototype.
    return (e as { toString(): string }).toString();
  } catch {
    return 'unreadable error';
  }
}

/** A request the client cancelled (unmount, superseded key): not an answer, not a failure. */
export function isAbortedError(e: unknown): boolean {
  return typeof e === 'object' && e !== null && (e as { code?: unknown }).code === 'aborted';
}

function requestIdOf(e: unknown): string | null {
  if (typeof e !== 'object' || e === null) return null;
  const id = (e as { requestId?: unknown }).requestId;
  return typeof id === 'string' && id !== '' ? id : null;
}

/**
 * The earliest moment after `nowMs` at which any source's phase can change on
 * its own (an answer ages past its maximum, or the watch window passes it), or
 * `null` when none is ahead. `useFreshness` sets ONE timer to this.
 */
export function nextFreshnessDeadline(
  sources: readonly FreshnessSource[],
  watchSinceMs: number,
  nowMs: number,
): number | null {
  let next = Number.POSITIVE_INFINITY;
  for (const source of sources) {
    const candidates = [watchSinceMs + source.maxAgeMs];
    if (source.dataUpdatedAt > 0) candidates.push(source.dataUpdatedAt + source.maxAgeMs);
    for (const at of candidates) {
      if (Number.isFinite(at) && at > nowMs && at < next) next = at;
    }
  }
  return Number.isFinite(next) ? next : null;
}

/**
 * Read one query. Rules, in order:
 * 1. an aborted error is ignored;
 * 2. a disabled query (`pending` + `idle`) is idle; placeholder data is checking;
 * 3. no data: an error → error-before-data; past the maximum age since the watch
 *    began → overdue-before-data; otherwise checking;
 * 4. data plus an error → error-after-data;
 * 5. data with freshness: age ≤ max → live; older, but watched for ≤ max →
 *    refreshing (a cached answer on mount or return); otherwise overdue;
 * 6. data without freshness → live.
 */
export function liveQueryState<T>(
  q: LiveQueryLike<T>,
  labels: { what: string; panel: string },
  freshness?: FreshnessClock & { maxAgeMs: number },
): LiveQueryState<T> {
  const error = q.error === null || q.error === undefined || isAbortedError(q.error) ? null : q.error;
  const requestId = requestIdOf(error);
  const base = { error, requestId };

  if (q.status === 'pending' && q.fetchStatus === 'idle') {
    return { ...base, phase: 'idle', data: undefined, lastGoodAtMs: null, message: null };
  }
  if (q.isPlaceholderData === true) {
    return { ...base, phase: 'checking', data: undefined, lastGoodAtMs: null, message: `Checking ${labels.what}…` };
  }

  const seconds = freshness === undefined ? 0 : Math.round(freshness.maxAgeMs / 1000);
  if (q.data === undefined) {
    if (error !== null) {
      return {
        ...base, phase: 'error-before-data', data: undefined, lastGoodAtMs: null,
        message: `Could not load ${labels.what}: ${describeError(error)}`,
      };
    }
    if (freshness !== undefined && freshness.nowMs - freshness.watchSinceMs > freshness.maxAgeMs) {
      return {
        ...base, phase: 'overdue-before-data', data: undefined, lastGoodAtMs: null,
        message: `${labels.panel} has not answered within ${seconds} s`,
      };
    }
    return { ...base, phase: 'checking', data: undefined, lastGoodAtMs: null, message: `Checking ${labels.what}…` };
  }

  const lastGoodAtMs = q.dataUpdatedAt > 0 ? q.dataUpdatedAt : null;
  const since = lastGoodAtMs === null ? 'its last answer' : formatLocalTime(lastGoodAtMs);
  if (error !== null) {
    return {
      ...base, phase: 'error-after-data', data: q.data, lastGoodAtMs,
      message: `${labels.panel} not refreshed since ${since} — ${describeError(error)}`,
    };
  }
  if (freshness !== undefined) {
    const age = freshness.nowMs - q.dataUpdatedAt;
    if (age <= freshness.maxAgeMs) {
      return { ...base, phase: 'live', data: q.data, lastGoodAtMs, message: null };
    }
    if (freshness.nowMs - freshness.watchSinceMs <= freshness.maxAgeMs) {
      return { ...base, phase: 'refreshing', data: q.data, lastGoodAtMs, message: `Refreshing ${labels.what}…` };
    }
    return {
      ...base, phase: 'overdue', data: q.data, lastGoodAtMs,
      message: `${labels.panel} not refreshed since ${since} — no answer within ${seconds} s`,
    };
  }
  return { ...base, phase: 'live', data: q.data, lastGoodAtMs, message: null };
}
